/** Dashboard: Was ist heute zu tun (Prozessdesign 04 §9 Nr. 1). */
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { SENDUNG_STATUS } from '@storeos/contracts';
import { dashboard } from '../api/lager';
import { datum, datumZeit, KLAERFALL_TEXT, SENDUNG_TEXT } from '../components/format';
import { RetoureBadge, SendungBadge } from '../components/Status';
import { Darf, Karte, Kachel, Seitenkopf, Tabelle, Zustand } from '../components/ui';
import { BelegHinweise } from '../components/BelegHinweise';

export function Dashboard() {
  const q = useQuery({ queryKey: ['dashboard'], queryFn: dashboard, refetchInterval: 30_000 });
  const nav = useNavigate();
  const d = q.data;
  return (
    <section>
      <Seitenkopf titel="Dashboard" unter={d ? `Heute: ${d.heute.importiert} importiert · ${d.zaehler_sendungen.bereit} bereit zum Packen · ${d.heute.label_erstellt} mit Label · ${d.heute.uebergeben} übergeben${d.packzeit.median_sek !== null ? ` · Packzeit Label→Scan Median ${d.packzeit.median_sek} s` : ''}` : undefined}>
        <Link className="knopf" to="/packstation">Zur Packstation</Link>
      </Seitenkopf>
      <Zustand laden={q.isLoading} fehler={q.error}>
        {d && (
          <>
            <div className="kacheln" data-testid="status-kacheln">
              {SENDUNG_STATUS.map((s) => (
                <Kachel key={s} wert={d.zaehler_sendungen[s] ?? 0} text={SENDUNG_TEXT[s]} klasse={`k-${s}`} onClick={() => nav(`/auftraege?sendung_status=${s}`)} />
              ))}
            </div>
            <BelegHinweise />
            {(d.klaerfaelle.some((k) => k.storno_alarm) || d.storno_nach_versand.length > 0) && (
              <Karte titel="Storno-Alarm" klasse="alarm">
                <ul className="liste-einfach storno-liste" data-testid="dashboard-storno">
                  {d.klaerfaelle.filter((k) => k.storno_alarm).map((k) => (
                    <li key={k.sendung_id}><Link to={`/auftraege/${k.order_id}`} className="mono">{k.bestellnummer}</Link><span>Ware zurück ins Fach</span><span className="hinweis kuerzen">{(k.text ?? '').replace(/^STORNO:\s*/, '')}</span></li>
                  ))}
                  {d.storno_nach_versand.map((k) => (
                    <li key={k.kennung}><Link to={`/auftraege/${k.order_id}`} className="mono">{k.kennung}</Link><span>storniert nach Übergabe</span><span className="hinweis">Paket ist unterwegs: Retoure bzw. Annahmeverweigerung erwarten</span></li>
                  ))}
                </ul>
              </Karte>
            )}
            <div className="raster-2">
              <Karte titel={`Klärfälle (${d.klaerfaelle.length})`}>
                <Tabelle testid="liste-klaerfaelle" zeilen={d.klaerfaelle} schluessel={(z) => z.sendung_id} onZeile={(z) => nav(`/auftraege/${z.order_id}`)} leer="Keine Klärfälle"
                  spalten={[
                    { kopf: 'Auftrag', zelle: (z) => z.bestellnummer, klasse: 'mono' },
                    { kopf: 'Grund', zelle: (z) => (z.storno_alarm ? <span className="warn-text">Storno</span> : z.grund ? KLAERFALL_TEXT[z.grund] : '–') },
                    { kopf: 'Notiz', zelle: (z) => <span className="kuerzen">{z.text ?? ''}</span> },
                    { kopf: 'Seit', zelle: (z) => datumZeit(z.seit) },
                  ]} />
              </Karte>
              <Karte titel={`Nachbestellen (${d.nachbestellen.length})`} aktion={<Link to="/artikel?nachbestellen=1" className="knopf-flach">Alle</Link>}>
                {d.bestand_verborgen && <p className="hinweis">Inventur läuft: Bestände sind bis zur Freigabe verborgen (Blindzählung).</p>}
                <Tabelle testid="liste-nachbestellen" zeilen={d.nachbestellen.slice(0, 12)} schluessel={(z) => z.article_id} onZeile={(z) => nav(`/artikel/${z.article_id}`)} leer="Alles über Meldebestand"
                  spalten={[
                    { kopf: 'SKU', zelle: (z) => z.sku, klasse: 'mono' },
                    { kopf: 'Artikel', zelle: (z) => <span className="kuerzen">{z.name}{z.variante ? ` · ${z.variante}` : ''}</span> },
                    { kopf: 'Verfügbar', zelle: (z) => <span className={z.verfuegbar < 0 ? 'negativ' : ''} data-testid="nb-verfuegbar">{z.verfuegbar}</span>, rechts: true },
                    { kopf: 'Melde', zelle: (z) => <span data-testid="nb-melde">{z.meldebestand}</span>, rechts: true },
                  ]} />
                {d.nachbestellen.length > 12 && <p className="hinweis">… und {d.nachbestellen.length - 12} weitere</p>}
              </Karte>
              <Karte titel={`Verspätet (${d.verspaetet.length})`}>
                <Tabelle zeilen={d.verspaetet.slice(0, 12)} schluessel={(z) => z.sendung_id} onZeile={(z) => nav(`/auftraege/${z.order_id}`)} leer="Nichts verspätet"
                  spalten={[
                    { kopf: 'Auftrag', zelle: (z) => z.bestellnummer, klasse: 'mono' },
                    { kopf: 'Status', zelle: (z) => <SendungBadge status={z.status} /> },
                    { kopf: 'Bezahlt', zelle: (z) => datum(z.bezahlt_at) },
                    { kopf: 'Werktage', zelle: (z) => z.werktage, rechts: true },
                  ]} />
                {d.verspaetet.length > 12 && <p className="hinweis">… und {d.verspaetet.length - 12} weitere (älteste zuerst)</p>}
              </Karte>
              <Karte titel={`Retourenfristen (${d.retourenfristen.length})`}>
                <Tabelle zeilen={d.retourenfristen} schluessel={(z) => z.return_id} onZeile={(z) => nav(`/retouren?q=${encodeURIComponent(z.nummer)}`)} leer="Keine offenen Retouren"
                  spalten={[
                    { kopf: 'Retoure', zelle: (z) => z.nummer, klasse: 'mono' },
                    { kopf: 'Auftrag', zelle: (z) => z.bestellnummer, klasse: 'mono' },
                    { kopf: 'Status', zelle: (z) => <RetoureBadge status={z.status} /> },
                    { kopf: 'Frist', zelle: (z) => <span className={new Date(z.frist_at).getTime() - Date.now() < 3 * 86_400_000 ? 'warn-text' : ''}>{datum(z.frist_at)}</span> },
                  ]} />
              </Karte>
              <Darf rolle="leitung">
                <Karte titel={`Outbox-Fehler (${d.outbox.fehlgeschlagen})`} klasse={d.outbox.fehlgeschlagen ? 'alarm' : ''} aktion={<Link to="/protokoll?tab=outbox&status=fehlgeschlagen" className="knopf-flach">Protokoll</Link>}>
                  <p className="hinweis">Offen {d.outbox.offen} · Trockenlauf {d.outbox.trockenlauf}</p>
                  <Tabelle zeilen={d.outbox.fehler} schluessel={(z) => z.id} leer="Keine Fehler"
                    spalten={[
                      { kopf: 'Typ', zelle: (z) => z.typ, klasse: 'mono' },
                      { kopf: 'Fehler', zelle: (z) => <span className="kuerzen">{z.letzter_fehler}</span> },
                      { kopf: 'Versuche', zelle: (z) => z.versuche, rechts: true },
                    ]} />
                </Karte>
              </Darf>
            </div>
          </>
        )}
      </Zustand>
    </section>
  );
}
